import { useTranslation } from "react-i18next";

import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { getCurrencyCode } from "../../utils/currency";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface AccountEditFinanceSectionProps {
  register: any;
  setValue: any;
  watch: any;
  accountStatusOpts: string[];
  currentStatus?: number;
}

export function AccountEditFinanceSection({
  register,
  setValue,
  watch,
  accountStatusOpts,
  currentStatus,
}: AccountEditFinanceSectionProps) {
  const { t } = useTranslation("accounts");
  const status = currentStatus ?? 2;
  const hasTme = CheckFeatureFlag("TME");

  const statusClassName = {
    0: "bg-amber-100 text-amber-600",
    1: "bg-red-100 text-red-600",
    2: "bg-green-100 text-green-600",
  }[status];

  const statusLabel = {
    0: t("Archived"),
    1: t("Disabled"),
    2: t("Enabled"),
  }[status];

  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="pb-1 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-800">{t("Finance")}</p>
          <p className="text-xs text-gray-500">{t("Status, VAT and banking")}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusClassName}`}>
          {statusLabel}
        </span>
      </div>
      <SelectInput
        label={t("Account Status")}
        register={register}
        registerName="status"
        setValue={setValue}
        watch={watch}
        options={accountStatusOpts.map((opt, i) => ({ id: i, name: opt }))}
        rounded
      />
      <TextInput
        label={t("VAT Number")}
        register={register}
        registerName="vatNumber"
        rounded
      />
      <div>
        <label className="text-sm font-medium text-gray-500">{t("Credit Limit")}</label>
        <div className="relative mt-1">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
            {getCurrencyCode()}
          </span>
          <input
            {...register("creditLimit")}
            className="w-full rounded-md border-1 border-gray-300 py-2 pl-12 pr-3 text-right text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>
      <div className="pt-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("Bank Details")}
        </p>
      </div>
      <TextInput
        label={t("Bank Account Name")}
        register={register}
        registerName="bankAccountName"
        rounded
      />
      <TextInput
        label={t("Sort Code")}
        register={register}
        registerName="bankSortCode"
        rounded
      />
      <TextInput
        label={t("Bank Account Number")}
        register={register}
        registerName="bankAccountNumber"
        rounded
      />
      {hasTme && (
        <>
          <div className="pt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("Oracle Billing")}
            </p>
            <p className="text-xs text-gray-500">
              {t("Used by the Oracle invoice export. Oracle identifies the customer by its own codes, so the export can't run for this account until the number, site and contact are set.")}
            </p>
          </div>
          <TextInput
            label={t("Oracle Customer Number")}
            register={register}
            registerName="oracleCustomerNumber"
            rounded
          />
          <TextInput
            label={t("Oracle Customer Site")}
            register={register}
            registerName="oracleCustomerSite"
            rounded
          />
          <TextInput
            label={t("Oracle Client Contact")}
            register={register}
            registerName="oracleContactName"
            rounded
          />
          <TextInput
            label={t("Oracle Contact Email")}
            register={register}
            registerName="oracleContactEmail"
            rounded
          />
          <TextInput
            label={t("Oracle Customer PO Reference")}
            register={register}
            registerName="oracleCustomerPoRef"
            rounded
          />
          <TextInput
            label={t("Oracle Line Item")}
            register={register}
            registerName="oracleLineItem"
            rounded
          />
          <p className="text-xs text-gray-500">
            {t("Only needed if this customer is billed on a different REC item to the rest of the site. Leave blank to use the site default.")}
          </p>
        </>
      )}
    </div>
  );
}
